import { BusinessType } from "../MoneyTransfer/BusinessType.tsx";
import { MoneyDTO } from "../ProductService/NormasTeachingProduct/useNormasTeachingProductById.ts";
import { PurchasedItemSnapshotDTO } from "../ProductService/PurchasedItem/PurchasedItemSnapshotDTO.ts";

type OrderCustomerInformation = {
  email: string;
  firstName: string;
  lastName: string;
};

export type OrderInvoiceAddressDTO = {
  street: string;
  province: string;
  city: string;
  cap: string;
  country?: string;
};

export type OrderInvoiceInfoDTO =
  | {
      requested: false;
    }
  | {
      requested: true;
      businessType: BusinessType.IndividualCompany;
      pec?: string;
      sdiCode?: string;
      vat: string;
      taxCode: string;
      address: OrderInvoiceAddressDTO;
    }
  | {
      requested: true;
      businessType: BusinessType.Person;
      taxCode: string;
      vat?: string;
      address: OrderInvoiceAddressDTO;
    }
  | {
      requested: true;
      businessType: BusinessType.Company;
      businessName: string;
      pec?: string;
      sdiCode?: string;
      vat: string;
      taxCode: string;
      address: OrderInvoiceAddressDTO;
    };

type BundleParticipant = {
  products: {
    id: string;
    priceInCents: MoneyDTO;
  }[];
  customer: OrderCustomerInformation;
};

export enum NormasTeachingItemType {
  Product = "product",
  Bundle = "bundle",
}

export enum PaymentType {
  OneTime = "one_time",
  Installments = "installments",
}

export enum PaymentMethod {
  Card = "stripe.card",
  SepaDebit = "stripe.sepa_debit",
  BankTransfer = "stripe.customer_balance",
  PayPal = "stripe.paypal",
  RevolutPay = "stripe.revolut_pay",
  Satispay = "stripe.satispay",
  ApplePay = "stripe.apple_pay",
  GooglePay = "stripe.google_pay",
  Unknown = "unknown",
}

export type StripeOrderDTO = {
  _id: string;
  clientSecret: string;
  normasTeachingProductId: string;
  customerInformation: OrderCustomerInformation;
  invoiceInfo: OrderInvoiceInfoDTO;
  productPrice: {
    amount: number;
    currency: string;
  };
  amountInCent: {
    amount: number;
    currency: string;
  };
  status: StripeOrderStatus;
  createdAt: string;
  paymentType: PaymentType;
  paymentMethod?: PaymentMethod;
  cardLast4Digits?: string;
  failureCode?: string;
  failureMessage?: string;
  numberOfInstallments?: number;
  numberOfPaidCharges: number;
  numberOfFailedCharges?: number;
  bundleParticipants?: BundleParticipant[];
  participants?: OrderCustomerInformation[];
  productType: NormasTeachingItemType;
  coupon?: {
    _id: string;
    couponCode: string;
    value: number;
  };
  installmentInvoices: {
    chargeId?: string;
    createdAt: Date;
    installmentNumber: number;
    status: "failed" | "paid";
    amountPaidInCents?: MoneyDTO;
    paymentMethod?: {
      method: PaymentMethod | string;
      cardLast4Digits?: string;
    };
    failureCode?: string;
    failureMessage?: string;
  }[];
  productSnapshot?: PurchasedItemSnapshotDTO;
  bankTransferSettlement?: StripeBankTransferSettlementDTO;
};

export type StripeBankTransferSettlementDTO = {
  totalAmountInCents: { amount: number; currency: string };
  amountAlreadyPaidInCents: { amount: number; currency: string };
  amountInCents: { amount: number; currency: string };
  receiptDateUtc: string;
  settledAtUtc: string;
  statusBeforeSettlement: StripeOrderStatus;
};

export enum StripeOrderStatus {
  Pending = "Pending",
  Succeeded = "Succeeded",
  AwaitingConfirmation = "awaiting_confirmation",
  PartiallyRefunded = "PartiallyRefunded",
  Refunded = "Refunded",
  InstallmentDelinquent = "InstallmentDelinquent",
  InstallmentPaid = "InstallmentPaid",
  SubscriptionCancelled = "SubscriptionCancelled",
  Failed = "Failed",
  InstallmentsSettledByBankTransfer = "InstallmentsSettledByBankTransfer",
}
